import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Button, Container } from 'reactstrap';
import { Waypoint } from 'react-waypoint';

import { showModal } from '../../../../actions';
import {
  getCollectionDestination,
  getFileDestination,
  getFilePlaceholderElement,
  normalizePageVisibilityMode,
} from '../../../../helpers/PageSections';
import {
  usePageId,
  usePageBasePath,
  usePageRouteId,
  usePageIsEditing,
  usePageVisibilityMode,
  usePageAllowedCollectionUuids,
} from '../../../../helpers/PageIdContext';
import formatIntl from '../Common/formatIntl';
import SectionHeader from '../Common/SectionHeader';
import SectionHeadingField from '../Common/SectionHeadingField';
import { withLayout } from '../PuckLayout';
import Loading from '../../../Loading';
import { ConnectedCollectionPicker } from './CollectionPicker';
import {
  CollectionFeedDownloadField,
  GridColumnsField,
  LimitField,
} from './PuckCollectionFeedFields';
import {
  FeedGrid,
  FeedMasonry,
  FeedTile,
} from './PuckCollectionFeedViews';
import { useLiveCollectionFeed } from './useLiveCollectionFeed';
import ViewMode from '../../../Filters/ViewMode';

const collectionShape = PropTypes.shape({
  id: PropTypes.string,
  uuid: PropTypes.string,
  name: PropTypes.string,
  featured_file: PropTypes.object,
  files: PropTypes.array,
});

const sortFiles = (files, orderby, order) => {
  const sorted = [...files].sort((a, b) => {
    let aVal;
    let bVal;
    if (orderby === 'name') {
      aVal = a.name || '';
      bVal = b.name || '';
      return order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    if (orderby === 'date') {
      aVal = new Date(a.created_at || 0).getTime();
      bVal = new Date(b.created_at || 0).getTime();
    } else {
      aVal = a.position ?? 0;
      bVal = b.position ?? 0;
    }
    return order === 'asc' ? aVal - bVal : bVal - aVal;
  });
  return sorted;
};

const buildFeedPlaceholders = (count = 4) => (
  Array.from({ length: count }, (_, index) => `feed-placeholder-${index + 1}`)
);

const PuckCollectionFeedRender = ({
  sectionHeading,
  collection: collectionProp,
  view,
  columns,
  orderby,
  order,
  limit,
  allowCollectionDownload,
  page_visibility_mode: pageVisibilityMode,
  onShowModal,
}) => {
  const intl = useIntl();
  const pageId = usePageId();
  const pageBasePath = usePageBasePath();
  const pageRouteId = usePageRouteId();
  const contextVisibilityMode = usePageVisibilityMode();
  const allowedCollectionUuids = usePageAllowedCollectionUuids();
  const effectivePageVisibilityMode = normalizePageVisibilityMode(
    pageVisibilityMode || contextVisibilityMode,
  );
  const location = useLocation();
  const isEditing = usePageIsEditing();
  const allowCollectionDownloadResolved = !!allowCollectionDownload;
  const openDownloadModal = useCallback(() => {
    if (!collectionProp) {
      return;
    }

    onShowModal('DownloadModal', {
      className: 'modal-download',
      files: [],
      collections: [collectionProp],
      pageId,
      backdrop: true,
    });
  }, [collectionProp, onShowModal, pageId]);

  const [displayView, setDisplayView] = useState(view || 'grid');

  useEffect(() => {
    setDisplayView(view || 'grid');
  }, [view]);

  const renderDisplayAction = useCallback((targetFile, actionView) => {
    if (isEditing || !allowCollectionDownloadResolved || !pageId || !targetFile?.uuid) {
      return null;
    }

    const label = intl.formatMessage({ defaultMessage: 'Download' });
    const actionId = `CollectionFeedDownload-${targetFile.uuid}-${actionView || view || 'grid'}`;

    return (
      <li>
        <a
          href="#download"
          id={actionId}
          aria-label={label}
          title={label}
          onClick={(e) => {
            e.preventDefault();
            onShowModal('DownloadModal', {
              className: 'modal-download',
              files: [targetFile],
              collections: [],
              fileCollectionMap: collectionProp?.uuid ? {
                [targetFile.uuid]: [collectionProp.uuid],
              } : {},
              pageId,
              backdrop: true,
            });
          }}
        >
          <i className="far fa-download" aria-hidden="true" />
        </a>
      </li>
    );
  }, [
    allowCollectionDownloadResolved,
    intl,
    isEditing,
    onShowModal,
    pageId,
    view,
    collectionProp?.uuid,
  ]);
  const {
    files: rawFiles,
    loading,
    loadingMore,
    hasMore,
    loadMore,
  } = useLiveCollectionFeed(
    collectionProp,
    pageId,
    limit,
    isEditing,
    allowedCollectionUuids,
    displayView,
  );

  const files = useMemo(() => {
    const sorted = sortFiles(rawFiles, orderby, order);
    const limited = limit > 0 ? sorted.slice(0, limit) : sorted;

    return limited.map((file) => ({
      ...file,
      to: getFileDestination({
        file,
        pageVisibilityMode: effectivePageVisibilityMode,
        pageBasePath,
        pageRouteId,
        pageId,
        backUrl: `${location.pathname}${location.search || ''}`,
      }),
    }));
  }, [
    rawFiles,
    orderby,
    order,
    limit,
    effectivePageVisibilityMode,
    pageBasePath,
    pageRouteId,
    pageId,
    location.pathname,
    location.search,
  ]);
  const placeholderColumns = Math.min(Math.max(columns || 3, 1), 6);
  const placeholderCount = placeholderColumns * 2;
  const placeholders = useMemo(() => buildFeedPlaceholders(placeholderCount), [placeholderCount]);
  const sectionTitle = sectionHeading?.title || '';
  const sectionSubtitle = sectionHeading?.subtitle || '';
  const headerAlign = sectionHeading?.align || 'left';
  const headerActions = (
    <>
      {!isEditing && collectionProp?.uuid && (
        <ViewMode
          size="sm"
          view={displayView || 'grid'}
          setView={setDisplayView}
          allowedViews={['grid', 'framed', 'masonry', 'tile']}
        />
      )}
      {!isEditing && pageId && collectionProp?.uuid && allowCollectionDownloadResolved && (
        <Button
          size="sm"
          color="secondary"
          className="puck-feed-header-btn"
          onClick={openDownloadModal}
        >
          <i className="far fa-download" /> {intl.formatMessage({ defaultMessage: 'Download all' })}
        </Button>
      )}
      {!isEditing && collectionProp?.uuid && (
        <Link
          to={getCollectionDestination({
            collection: collectionProp,
            pageVisibilityMode: effectivePageVisibilityMode,
            pageBasePath,
            pageRouteId,
          })}
          className="btn btn-outline-secondary btn-sm puck-feed-header-btn"
        >
          {intl.formatMessage({ defaultMessage: 'Show all' })}
        </Link>
      )}
    </>
  );
  const header = (
    <SectionHeader
      title={sectionTitle}
      subtitle={sectionSubtitle}
      align={headerAlign}
      actions={headerActions}
    />
  );

  if (!collectionProp) {
    return (
      <div className="custom-page-section">
        <Container className="puck-feed puck-feed--empty">
          <div className="puck-feed-header">{header}</div>
          {isEditing && (
            <div className="puck-component-info-text mb-4">
              {intl.formatMessage({ defaultMessage: 'No collection selected. Pick a collection in component settings.' })}
            </div>
          )}
          <div
            className="puck-feed-placeholder-grid"
            style={{ gridTemplateColumns: `repeat(${placeholderColumns}, minmax(0, 1fr))` }}
          >
            {placeholders.map((placeholderId) => (
              <div key={placeholderId} className="puck-feed-placeholder-item">
                {getFilePlaceholderElement({ className: 'puck-feed-file-placeholder' })}
              </div>
            ))}
          </div>
        </Container>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="custom-page-section">
        <div className="puck-feed puck-feed--empty center">
          <Loading />
        </div>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="custom-page-section">
        <Container className="puck-feed puck-feed--empty">
          <div className="puck-feed-header">{header}</div>
          <div className="puck-feed-placeholder-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
            {buildFeedPlaceholders(3).map((placeholderId) => (
              <div key={placeholderId} className="puck-feed-placeholder-item">
                {getFilePlaceholderElement({ className: 'puck-feed-file-placeholder' })}
              </div>
            ))}
          </div>
          {intl.formatMessage({ defaultMessage: 'No files in this Collection.' })}
        </Container>
      </div>
    );
  }

  return (
    <div className="custom-page-section">
      <Container className="puck-feed">
        <div className="puck-feed-header">{header}</div>
        {displayView === 'grid' && <FeedGrid files={files} columns={columns} renderDisplayAction={renderDisplayAction} />}
        {displayView === 'framed' && <FeedGrid files={files} columns={columns} renderDisplayAction={renderDisplayAction} selectedView="framed" />}
        {displayView === 'masonry' && <FeedMasonry files={files} renderDisplayAction={renderDisplayAction} />}
        {displayView === 'tile' && <FeedTile files={files} renderDisplayAction={renderDisplayAction} />}
        {!isEditing && hasMore && (
          <Waypoint onEnter={loadMore} bottomOffset="-200px" />
        )}
        {loadingMore && (
        <div className="puck-feed-loading-more center">
          <Loading />
        </div>
        )}
        {isEditing && limit && limit > 36 && (
          <div className="puck-component-info-text mb-4 center">
            {intl.formatMessage({ defaultMessage: 'Preview limited to 36 files.' })}
          </div>
        )}
      </Container>
    </div>
  );
};

PuckCollectionFeedRender.propTypes = {
  sectionHeading: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    align: PropTypes.oneOf(['left', 'center', 'right']),
  }),
  collection: collectionShape,
  view: PropTypes.string,
  columns: PropTypes.number,
  orderby: PropTypes.string,
  order: PropTypes.string,
  limit: PropTypes.number,
  allowCollectionDownload: PropTypes.bool,
  page_visibility_mode: PropTypes.string,
  onShowModal: PropTypes.func.isRequired,
};

PuckCollectionFeedRender.defaultProps = {
  sectionHeading: null,
  collection: null,
  view: 'grid',
  columns: 3,
  orderby: 'position',
  order: 'asc',
  limit: 8,
  allowCollectionDownload: true,
  page_visibility_mode: null,
};

const mapDispatchToProps = (dispatch) => ({
  onShowModal: (modalName, params) => dispatch(showModal(modalName, params)),
});

const ConnectedPuckCollectionFeedRender = connect(null, mapDispatchToProps)(
  PuckCollectionFeedRender,
);

const PuckCollectionFeedConfig = {
  fields: {
    sectionHeading: {
      type: 'custom',
      get label() { return formatIntl('Section heading'); },
      render: (props) => <SectionHeadingField {...props} />,
    },
    collection: {
      type: 'custom',
      get label() { return formatIntl('Collection'); },
      render: ({ value, onChange }) => (
        <ConnectedCollectionPicker value={value} onChange={onChange} />
      ),
    },
    view: {
      type: 'select',
      get label() { return formatIntl('View'); },
      options: [
        { get label() { return formatIntl('Grid'); }, value: 'grid' },
        { get label() { return formatIntl('Framed'); }, value: 'framed' },
        { get label() { return formatIntl('Masonry'); }, value: 'masonry' },
        { get label() { return formatIntl('Tile'); }, value: 'tile' },
      ],
    },
    columns: {
      type: 'custom',
      get label() { return formatIntl('Columns in grid'); },
      render: (props) => <GridColumnsField {...props} />,
    },
    orderby: {
      type: 'select',
      get label() { return formatIntl('Sort by'); },
      options: [
        { get label() { return formatIntl('Position'); }, value: 'position' },
        { get label() { return formatIntl('Date'); }, value: 'date' },
        { get label() { return formatIntl('Name'); }, value: 'name' },
      ],
    },
    order: {
      type: 'radio',
      get label() { return formatIntl('Order'); },
      options: [
        { get label() { return formatIntl('Ascending'); }, value: 'asc' },
        { get label() { return formatIntl('Descending'); }, value: 'desc' },
      ],
    },
    limit: {
      type: 'custom',
      get label() { return formatIntl('Limit'); },
      render: (props) => <LimitField {...props} />,
    },
    allowCollectionDownload: {
      type: 'custom',
      get label() { return formatIntl('Collection download'); },
      options: [
        { get label() { return formatIntl('Yes'); }, value: true },
        { get label() { return formatIntl('No'); }, value: false },
      ],
      render: (props) => <CollectionFeedDownloadField {...props} />,
    },
  },

  defaultProps: {
    sectionHeading: {
      title: formatIntl('Header'),
      subtitle: formatIntl('Subheader'),
      align: 'left',
    },
    collection: null,
    view: 'grid',
    columns: 4,
    orderby: 'position',
    order: 'asc',
    limit: 8,
    allowCollectionDownload: true,
    layout: { padding: '0px' },
  },

  render: (props) => <ConnectedPuckCollectionFeedRender {...props} />,
};

export default withLayout(PuckCollectionFeedConfig);
